import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ProgrammeCard } from "@/components/learning/ProgrammeCard";
import { api } from "@/lib/api";
import { SCHOOLS, STATUS_META } from "@/lib/learning";
import { journeyMonths, MONTHS } from "@/lib/expeditions";
import { useTrainingDiscount } from "@/hooks/useActiveMembership";

const FORMATS = ["FIELD", "CLASSROOM + PRACTICE", "HYBRID", "ONLINE / HYBRID"];
const TERRAINS = ["LAND", "WATER", "MOUNTAIN", "AIR"];
const STATUSES = ["OFFERED", "DELIVERED", "COMING_SOON", "CUSTOM"];
const CERTS = [["YES", "Certificate available"], ["DEPENDS", "Depends on edition"], ["NO", "No certificate"]];
const selectCls = "w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text";

function FilterPanel({ f, setF, subcats, certOptions, levels, years }) {
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const Select = ({ k, label, options, testid }) => (
    <label className="block">
      <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">{label}</span>
      <select data-testid={testid} value={f[k]} onChange={set(k)} className={`${selectCls} mt-1`}>
        <option value="">All</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
  const active = Object.values(f).some(Boolean);
  return (
    <div className="space-y-4" data-testid="training-filters">
      <Select k="category" label="Category" testid="filter-category" options={SCHOOLS.map((s) => [s.slug, s.name])} />
      {subcats.length > 0 && <Select k="subcategory" label="Subcategory" testid="filter-subcategory" options={subcats.map((s) => [s, s])} />}
      <Select k="status" label="Status" testid="filter-status" options={STATUSES.map((s) => [s, STATUS_META[s].label])} />
      <Select k="format" label="Format (Online / Field / Hybrid)" testid="filter-format" options={FORMATS.map((s) => [s, s])} />
      <Select k="terrain" label="Land / Water / Mountain / Air" testid="filter-terrain" options={TERRAINS.map((s) => [s, s])} />
      {levels.length > 0 && <Select k="level" label="Skill level" testid="filter-level" options={levels.map((s) => [s, s])} />}
      <Select k="scope" label="India / World" testid="filter-scope" options={[["INDIA", "India"], ["WORLD", "World"]]} />
      <Select k="month" label="Month (upcoming dates)" testid="filter-month" options={MONTHS.map((m, i) => [String(i + 1), m])} />
      {years.length > 0 && <Select k="year" label="Year" testid="filter-year" options={years.map((y) => [String(y), String(y)])} />}
      <Select k="volunteer" label="Volunteer opportunity" testid="filter-volunteer" options={[["YES", "Volunteer option available"]]} />
      {certOptions.length > 1 && <Select k="certificate" label="Certificate" testid="filter-certificate" options={certOptions} />}
      {active && (
        <button
          type="button"
          data-testid="filters-clear"
          onClick={() => setF({ category: "", subcategory: "", status: "", format: "", terrain: "", certificate: "", level: "", scope: "", month: "", year: "", volunteer: "" })}
          className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-secondary hover:text-ptf-primary"
        >
          <X className="h-3.5 w-3.5" /> Clear filters
        </button>
      )}
    </div>
  );
}

export default function TrainingCatalogue() {
  const [params, setParams] = useSearchParams();
  const { data: programmes = [], isLoading } = useQuery({ queryKey: ["training"], queryFn: async () => (await api.get("/training")).data });
  const [q, setQ] = useState(params.get("q") || "");
  const [f, setF] = useState({
    category: params.get("category") || "", subcategory: "", status: params.get("status") || "",
    format: "", terrain: "", certificate: "", level: "", scope: "", month: "", year: "", volunteer: "",
  });

  const subcats = useMemo(() => {
    if (!f.category) return [];
    return [...new Set(programmes.filter((p) => p.category === f.category && p.subcategory).map((p) => p.subcategory))];
  }, [programmes, f.category]);

  const certOptions = useMemo(
    () => CERTS.filter(([v]) => programmes.some((p) => p.certificate === v)),
    [programmes]
  );
  const levels = useMemo(() => [...new Set(programmes.map((p) => p.level).filter(Boolean))].sort(), [programmes]);
  const years = useMemo(() => [...new Set(programmes.flatMap((p) => (p.editions || []).flatMap((ed) => journeyMonths(ed).map((x) => x.year))))].sort(), [programmes]);

  const [limit, setLimit] = useState(48);
  useEffect(() => { setLimit(48); }, [q, f]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return programmes.filter((p) => {
      if (f.category && p.category !== f.category) return false;
      if (f.subcategory && p.subcategory !== f.subcategory) return false;
      if (f.status && p.status !== f.status) return false;
      if (f.format && p.format !== f.format) return false;
      if (f.terrain && p.terrain !== f.terrain) return false;
      if (f.certificate && p.certificate !== f.certificate) return false;
      if (f.level && p.level !== f.level) return false;
      if (f.scope === "INDIA" && (p.country || "India") !== "India") return false;
      if (f.scope === "WORLD" && (p.country || "India") === "India") return false;
      if (f.volunteer && !p.volunteer_option) return false;
      if (f.month || f.year) {
        const ms = (p.editions || []).flatMap((ed) => journeyMonths(ed));
        if (f.month && !ms.some((x) => x.month === +f.month && (!f.year || x.year === +f.year))) return false;
        if (f.year && !f.month && !ms.some((x) => x.year === +f.year)) return false;
      }
      if (needle && !(`${p.title} ${p.subcategory} ${p.description}`.toLowerCase().includes(needle))) return false;
      return true;
    });
  }, [programmes, q, f]);

  const applyCategory = (slug) => {
    setF((prev) => ({ ...prev, category: prev.category === slug ? "" : slug, subcategory: "" }));
    setParams(slug ? { category: slug } : {}, { replace: true });
  };

  const { eligible: discountEligible, percent: discountPercent, source: discountSource } = useTrainingDiscount();

  return (
    <>
      <Seo title="Training Catalogue" path="/learning/training" description="Browse Planet Trotter's full training catalogue: first aid and CPR awareness, adventure and outdoor skills, conservation, wildlife, responsible tourism, leadership, agriculture, research and more. Filter by category, status, format and terrain." />
      <PageHero
        eyebrow="Field Learning · Training"
        titleLines={["What do you", "want to learn?"]}
        intro="Every programme carries an honest status: Delivered / Documented, Currently Offered, Coming Soon, or Custom / Group Programme."
        image="/dawn/vor-trek.jpg"
      />

      <section className="bg-ptf-bg py-12 md:py-16">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          {discountEligible && (
            <p data-testid="member-training-discount-banner" className="mb-8 border border-ptf-secondary/30 bg-ptf-secondary/[0.05] px-5 py-4 text-sm text-ptf-secondary leading-relaxed">
              {discountSource === "dawn" ? "As a paid DAWN applicant" : "As an active Foundation member"}, you get {discountPercent}% off every eligible paid Training programme, applied automatically at checkout.
            </p>
          )}
          <div className="flex flex-col md:flex-row gap-3 md:items-center mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ptf-text/40" />
              <input
                data-testid="training-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder='What do you want to learn? Try "CPR", "Kayaking", "Wildlife", "Leadership"'
                className="w-full bg-white border border-ptf-primary/20 pl-11 pr-4 py-3.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text placeholder:text-ptf-text/40"
              />
            </div>
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filters-open" className="inline-flex items-center gap-2 border border-ptf-primary/25 bg-white px-5 py-3.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-ptf-primary w-full justify-center">
                    <SlidersHorizontal className="h-4 w-4" /> Filters
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-ptf-bg overflow-y-auto w-[320px]">
                  <SheetTitle className="font-display uppercase tracking-tight text-ptf-primary mb-5">Filter programmes</SheetTitle>
                  <FilterPanel f={f} setF={setF} subcats={subcats} certOptions={certOptions} levels={levels} years={years} />
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="hidden lg:flex flex-wrap gap-2 mb-8" data-testid="category-chips">
            {SCHOOLS.map((s) => (
              <button
                key={s.slug}
                type="button"
                data-testid={`chip-${s.slug}`}
                onClick={() => applyCategory(s.slug)}
                className={`text-[0.62rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${f.category === s.slug ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/20 text-ptf-text/65 hover:border-ptf-primary hover:text-ptf-primary"}`}
              >
                {s.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <aside className="hidden lg:block lg:col-span-3 bg-white border border-ptf-primary/12 p-6 sticky top-28">
              <FilterPanel f={f} setF={setF} subcats={subcats} certOptions={certOptions} levels={levels} years={years} />
            </aside>
            <div className="lg:col-span-9">
              <p data-testid="results-count" className="text-[0.68rem] uppercase tracking-[0.18em] text-ptf-text/55 mb-5">
                {isLoading ? "Loading the catalogue" : `${results.length} programme${results.length === 1 ? "" : "s"}`}
              </p>
              {!isLoading && results.length === 0 && (
                <p data-testid="no-results" className="text-ptf-text/60 py-12">No programmes match. Try clearing a filter or searching differently.</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {results.slice(0, limit).map((p, i) => (
                  <Reveal key={p.slug} delay={(i % 6) * 0.03} className="h-full">
                    <ProgrammeCard p={p} index={i} />
                  </Reveal>
                ))}
              </div>
              {results.length > limit && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    data-testid="load-more-btn"
                    onClick={() => setLimit((l) => l + 48)}
                    className="border border-ptf-primary/30 text-ptf-primary px-8 py-3 text-[0.7rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-primary hover:text-ptf-bg transition-colors"
                  >
                    Load More ({results.length - limit} remaining)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
